import { useState, type FormEvent } from 'react'
import { isApplicationEndpointConfigured, submitPartnerApplication } from '../utils/partnerApi'
import { trackEvent } from '../utils/analytics'

export type ApplicationValues = {
  companyName: string
  contactName: string
  workEmail: string
  website: string
  category: string
  description: string
  interest: string
}

export type ApplicationField = keyof ApplicationValues
export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY: ApplicationValues = {
  companyName: '',
  contactName: '',
  workEmail: '',
  website: '',
  category: '',
  description: '',
  interest: '',
}

export const DESCRIPTION_MAX = 600
const FIELD_ORDER: ApplicationField[] = ['companyName', 'contactName', 'workEmail', 'website', 'category', 'description', 'interest']

function isValidWebsite(value: string) {
  try {
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`)
    return url.hostname.includes('.')
  } catch {
    return false
  }
}

function validate(values: ApplicationValues) {
  const errors: Partial<Record<ApplicationField, string>> = {}
  if (!values.companyName.trim()) errors.companyName = 'Enter your company name.'
  if (!values.contactName.trim()) errors.contactName = 'Enter a contact name.'
  if (!values.workEmail.trim()) errors.workEmail = 'Enter your work email.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.workEmail.trim())) errors.workEmail = 'Enter a valid email address.'
  if (!values.website.trim()) errors.website = 'Enter your company website.'
  else if (!isValidWebsite(values.website.trim())) errors.website = 'Enter a valid website, e.g. example.com.'
  if (!values.category) errors.category = 'Choose a product category.'
  if (!values.description.trim()) errors.description = 'Add a brief description.'
  else if (values.description.length > DESCRIPTION_MAX) errors.description = `Keep it under ${DESCRIPTION_MAX} characters.`
  if (!values.interest) errors.interest = 'Choose a partnership interest.'
  return errors
}

export function fieldId(field: ApplicationField) {
  return `partner-app-${field}`
}

export function usePartnerApplicationForm() {
  const [values, setValues] = useState<ApplicationValues>(EMPTY)
  const [errors, setErrors] = useState<Partial<Record<ApplicationField, string>>>({})
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [submitError, setSubmitError] = useState<'unavailable' | 'failed' | null>(null)

  const setField = (field: ApplicationField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    if (errors[field]) setErrors(({ [field]: _removed, ...rest }) => rest)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (status === 'submitting') return
    const nextErrors = validate(values)
    setErrors(nextErrors)
    const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field])
    if (firstInvalid) {
      document.getElementById(fieldId(firstInvalid))?.focus()
      return
    }

    setStatus('submitting')
    setSubmitError(null)
    try {
      await submitPartnerApplication({
        ...values,
        companyName: values.companyName.trim(),
        contactName: values.contactName.trim(),
        workEmail: values.workEmail.trim(),
        website: values.website.trim(),
        description: values.description.trim(),
        sourcePage: window.location.pathname,
      })
      setStatus('success')
      // Only non-personal fields are sent to analytics.
      trackEvent('partner_application_submit', { category: values.category, interest: values.interest })
    } catch {
      setStatus('error')
      setSubmitError(isApplicationEndpointConfigured() ? 'failed' : 'unavailable')
    }
  }

  const reset = () => {
    setValues(EMPTY)
    setErrors({})
    setStatus('idle')
    setSubmitError(null)
  }

  return { values, errors, status, submitError, setField, submit, reset }
}
